import type { ReactElement } from 'react'

import { PillButton } from '@maria-cavalheiro-website/shared-ui-web'

import type { ResolvedTheme } from './use-theme.ts'

/**
 * The label names the ACTION, not the state.
 *
 * A toggle labelled with what it currently is ("Tema escuro") is ambiguous when
 * read out of context — is that a description or a promise? Naming the action
 * resolves it.
 */
const LABEL_TO_DARK = 'Ativar tema escuro'
const LABEL_TO_LIGHT = 'Ativar tema claro'

export interface ThemeToggleProps {
  readonly resolved: ResolvedTheme
  readonly onToggle: () => void
}

/**
 * Pure presenter. The theme itself is owned by `app.tsx`, which calls `useTheme`
 * exactly once — the sky reads the same resolved value, and one source of truth is
 * the only way both stay in step when the reader toggles.
 *
 * Renders ONE icon. The design rendered both a sun and a moon and toggled their
 * visibility by flipping a custom property between `none` and `inline-flex` —
 * application logic living in the cascade. One conditional icon says the same
 * thing where it can be read.
 *
 * Deliberately NOT `aria-pressed`: this is not a two-state toggle over a single
 * concept, it swaps which theme is active, and its label changes with it.
 * `aria-pressed` plus a changing label double-announces.
 */
export function ThemeToggle({ resolved, onToggle }: ThemeToggleProps): ReactElement {
  const isDark = resolved === 'escuro'
  const label = isDark ? LABEL_TO_LIGHT : LABEL_TO_DARK

  return (
    <PillButton variant="secondary" onClick={onToggle} icon={isDark ? 'sun' : 'moon'} label={label}>
      <span className="visually-hidden">{label}</span>
    </PillButton>
  )
}
